import React, { useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import ChatWidget from "./ChatWidget";

// Standalone widget without authentication
const ChatWidgetStandalone = () => {
  // Get customization from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const primaryColor = urlParams.get('primaryColor') || '#0000ff';
  const secondaryColor = urlParams.get('secondaryColor') || '#000000';
  const position = urlParams.get('position') || 'bottom-right'; // bottom-right, bottom-left, top-right, top-left

  const theme = createTheme({
    palette: {
      primary: {
        main: primaryColor,
        light: lightenColor(primaryColor, 30),
        dark: darkenColor(primaryColor, 20),
      },
      secondary: {
        main: secondaryColor,
        light: lightenColor(secondaryColor, 30),
        dark: darkenColor(secondaryColor, 20),
      },
      background: {
        default: "transparent",
        paper: "#ffffff",
      },
      text: {
        primary: "#000000",
        secondary: "#666666",
      },
    },
  });

  // Send hover messages to parent
  useEffect(() => {
    const isInIframe = window.self !== window.top;
    console.log('Widget: Is in iframe?', isInIframe);
    
    const handleMouseEnter = () => {
      console.log('Widget: Sending hover=true to parent');
      if (isInIframe) {
        window.parent.postMessage({ type: 'widget-hover', hover: true }, '*');
      }
    };
    const handleMouseLeave = () => {
      console.log('Widget: Sending hover=false to parent');
      if (isInIframe) {
        window.parent.postMessage({ type: 'widget-hover', hover: false }, '*');
      }
    };
    const handleClick = () => {
      console.log('Widget: Button clicked, sending hover=true');
      if (isInIframe) {
        window.parent.postMessage({ type: 'widget-hover', hover: true }, '*');
      }
    };

    const attachListeners = () => {
      const button = document.querySelector('.chat-widget-button');
      const modal = document.querySelector('.chat-widget-modal');
      console.log('Widget: Attaching listeners - button:', !!button, 'modal:', !!modal);

      if (button) {
        button.addEventListener('mouseenter', handleMouseEnter);
        button.addEventListener('mouseleave', handleMouseLeave);
        button.addEventListener('click', handleClick);
        console.log('Widget: Button listeners attached');
      }
      if (modal) {
        modal.addEventListener('mouseenter', handleMouseEnter);
        modal.addEventListener('mouseleave', handleMouseLeave);
        console.log('Widget: Modal listeners attached');
      }

      return { button, modal };
    };

    // Try immediately and retry with multiple delays
    const immediate = attachListeners();
    const timeout1 = setTimeout(attachListeners, 100);
    const timeout2 = setTimeout(attachListeners, 500);
    const timeout3 = setTimeout(attachListeners, 1000);
    const timeout4 = setTimeout(attachListeners, 2000);
    const timeout5 = setTimeout(attachListeners, 3000);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
      clearTimeout(timeout5);
      if (immediate.button) {
        immediate.button.removeEventListener('mouseenter', handleMouseEnter);
        immediate.button.removeEventListener('mouseleave', handleMouseLeave);
      }
      if (immediate.modal) {
        immediate.modal.removeEventListener('mouseenter', handleMouseEnter);
        immediate.modal.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  // Apply position styles
  useEffect(() => {
    const button = document.querySelector('.chat-widget-button');
    const modal = document.querySelector('.chat-widget-modal');
    
    if (button && modal) {
      // Reset all positions
      button.style.top = 'auto';
      button.style.bottom = 'auto';
      button.style.left = 'auto';
      button.style.right = 'auto';
      modal.style.top = 'auto';
      modal.style.bottom = 'auto';
      modal.style.left = 'auto';
      modal.style.right = 'auto';

      // Apply position based on parameter
      switch(position) {
        case 'bottom-left':
          button.style.bottom = '24px';
          button.style.left = '24px';
          modal.style.bottom = '24px';
          modal.style.left = '24px';
          break;
        case 'top-right':
          button.style.top = '24px';
          button.style.right = '24px';
          modal.style.top = '24px';
          modal.style.right = '24px';
          break;
        case 'top-left':
          button.style.top = '24px';
          button.style.left = '24px';
          modal.style.top = '24px';
          modal.style.left = '24px';
          break;
        default: // bottom-right
          button.style.bottom = '24px';
          button.style.right = '24px';
          modal.style.bottom = '24px';
          modal.style.right = '24px';
      }
    }
  }, [position]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div style={{ 
        width: '100vw', 
        height: '100vh', 
        background: 'transparent',
        overflow: 'hidden'
      }}>
        <ChatWidget userName="Guest" />
      </div>
    </ThemeProvider>
  );
};

// Helper functions to lighten/darken colors
function lightenColor(color, percent) {
  const num = parseInt(color.replace("#",""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 +
    (G<255?G<1?0:G:255)*0x100 + (B<255?B<1?0:B:255))
    .toString(16).slice(1);
}

function darkenColor(color, percent) {
  const num = parseInt(color.replace("#",""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return "#" + (0x1000000 + (R>0?R:0)*0x10000 +
    (G>0?G:0)*0x100 + (B>0?B:0))
    .toString(16).slice(1);
}

export default ChatWidgetStandalone;
